import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Calendar, Image, LogOut, Trash2, CheckCircle, Clock, XCircle, 
  Users, Camera, Plus, RefreshCw, ChevronDown, ChevronUp, Dog
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [stats, setStats] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [newPhoto, setNewPhoto] = useState({ dogName: '', photoUrl: '', caption: '' });
  const navigate = useNavigate();

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
  });

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [bookingsRes, photosRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`, getAuthHeaders()),
        axios.get(`${API}/admin/photos`, getAuthHeaders()),
        axios.get(`${API}/admin/stats`, getAuthHeaders())
      ]);
      setBookings(bookingsRes.data);
      setPhotos(photosRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
        toast.error('Session expired. Please login again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
    toast.success('Logged out successfully');
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}`, { status: newStatus }, getAuthHeaders());
      toast.success(`Booking ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`, getAuthHeaders());
      toast.success('Booking deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  const uploadPhoto = async (e) => {
    e.preventDefault();
    if (!newPhoto.dogName || !newPhoto.photoUrl) {
      toast.error('Dog name and photo URL are required');
      return;
    }
    try {
      await axios.post(`${API}/admin/photos`, newPhoto, getAuthHeaders());
      toast.success('Photo uploaded');
      setNewPhoto({ dogName: '', photoUrl: '', caption: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  const deletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    try {
      await axios.delete(`${API}/admin/photos/${photoId}`, getAuthHeaders());
      toast.success('Photo deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <CheckCircle className="h-4 w-4" />;
      case 'cancelled': return <XCircle className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-primary-foreground shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Admin Dashboard</h1>
              <p className="text-sm opacity-80">Sadie's Mission Dog Boarding</p>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={fetchData} className="text-primary-foreground hover:bg-primary-foreground/20">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-primary-foreground hover:bg-primary-foreground/20">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Bookings</p>
                  <p className="text-3xl font-bold">{stats.totalBookings || 0}</p>
                </div>
                <Calendar className="h-10 w-10 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <p className="text-3xl font-bold text-yellow-600">{stats.pendingBookings || 0}</p>
                </div>
                <Clock className="h-10 w-10 text-yellow-600 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Confirmed</p>
                  <p className="text-3xl font-bold text-green-600">{stats.confirmedBookings || 0}</p>
                </div>
                <CheckCircle className="h-10 w-10 text-green-600 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Photos</p>
                  <p className="text-3xl font-bold">{stats.totalPhotos || 0}</p>
                </div>
                <Camera className="h-10 w-10 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'bookings' ? 'default' : 'outline'}
            onClick={() => setActiveTab('bookings')}
          >
            <Calendar className="h-4 w-4 mr-2" />
            Bookings
          </Button>
          <Button
            variant={activeTab === 'photos' ? 'default' : 'outline'}
            onClick={() => setActiveTab('photos')}
          >
            <Image className="h-4 w-4 mr-2" />
            Photos
          </Button>
        </div>

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  All Bookings ({bookings.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {bookings.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No bookings yet</p>
                ) : (
                  <div className="space-y-3">
                    {bookings.map((booking) => (
                      <div key={booking.id} className="border rounded-lg overflow-hidden">
                        <div 
                          className="p-4 bg-card hover:bg-secondary/30 cursor-pointer transition-colors"
                          onClick={() => setExpandedBooking(expandedBooking === booking.id ? null : booking.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(booking.status || 'pending')}`}>
                                {getStatusIcon(booking.status || 'pending')}
                                {booking.status || 'pending'}
                              </div>
                              <div>
                                <p className="font-semibold">{booking.ownerInfo?.firstName} {booking.ownerInfo?.lastName}</p>
                                <p className="text-sm text-muted-foreground">
                                  {booking.checkInDate} - {booking.checkOutDate}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">
                                {booking.dogs?.length || 0} dog(s)
                              </span>
                              {expandedBooking === booking.id ? (
                                <ChevronUp className="h-5 w-5" />
                              ) : (
                                <ChevronDown className="h-5 w-5" />
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {expandedBooking === booking.id && (
                          <div className="p-4 bg-secondary/20 border-t">
                            <div className="grid md:grid-cols-2 gap-4 mb-4">
                              <div>
                                <h4 className="font-semibold mb-2">Contact Info</h4>
                                <p className="text-sm"><strong>Email:</strong> {booking.ownerInfo?.email}</p>
                                <p className="text-sm"><strong>Phone:</strong> {booking.ownerInfo?.phone}</p>
                              </div>
                              <div>
                                <h4 className="font-semibold mb-2">Stay Details</h4>
                                <p className="text-sm"><strong>Check-in:</strong> {booking.checkInDate} at {booking.checkInTime}</p>
                                <p className="text-sm"><strong>Check-out:</strong> {booking.checkOutDate} at {booking.checkOutTime}</p>
                              </div>
                            </div>
                            
                            {booking.dogs && booking.dogs.length > 0 && (
                              <div className="mb-4">
                                <h4 className="font-semibold mb-2 flex items-center gap-2">
                                  <Dog className="h-4 w-4" />
                                  Dogs
                                </h4>
                                <div className="grid gap-2">
                                  {booking.dogs.map((dog, idx) => (
                                    <div key={idx} className="bg-background p-3 rounded-lg text-sm">
                                      <p><strong>{dog.name}</strong> - {dog.breed}, {dog.age}</p>
                                      {dog.behaviorNotes && <p className="text-muted-foreground mt-1">{dog.behaviorNotes}</p>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {booking.addOns && booking.addOns.length > 0 && (
                              <div className="mb-4">
                                <h4 className="font-semibold mb-2">Add-ons</h4>
                                <div className="flex flex-wrap gap-2">
                                  {booking.addOns.map((addon, idx) => (
                                    <span key={idx} className="px-2 py-1 bg-primary/10 text-primary rounded text-xs">
                                      {addon}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            <div className="flex items-center gap-2 pt-4 border-t">
                              <Label className="text-sm">Status:</Label>
                              <Select 
                                value={booking.status || 'pending'} 
                                onValueChange={(value) => updateBookingStatus(booking.id, value)}
                              >
                                <SelectTrigger className="w-40">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="confirmed">Confirmed</SelectItem>
                                  <SelectItem value="cancelled">Cancelled</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button 
                                variant="destructive" 
                                size="sm"
                                onClick={() => deleteBooking(booking.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Photos Tab */}
        {activeTab === 'photos' && (
          <div className="space-y-6">
            {/* Upload Form */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5" />
                  Add New Photo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={uploadPhoto} className="space-y-4">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="dogName">Dog Name</Label>
                      <Input
                        id="dogName"
                        value={newPhoto.dogName}
                        onChange={(e) => setNewPhoto({ ...newPhoto, dogName: e.target.value })}
                        placeholder="e.g., Envy"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="photoUrl">Photo URL</Label>
                      <Input
                        id="photoUrl"
                        value={newPhoto.photoUrl}
                        onChange={(e) => setNewPhoto({ ...newPhoto, photoUrl: e.target.value })}
                        placeholder="https://..."
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="caption">Caption (optional)</Label>
                      <Input
                        id="caption"
                        value={newPhoto.caption}
                        onChange={(e) => setNewPhoto({ ...newPhoto, caption: e.target.value })}
                        placeholder="Fun at the pond!"
                      />
                    </div>
                  </div>
                  <Button type="submit">
                    <Plus className="h-4 w-4 mr-2" />
                    Upload Photo
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Photos Grid */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Camera className="h-5 w-5" />
                  All Photos ({photos.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {photos.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No photos yet</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {photos.map((photo) => (
                      <div key={photo.id} className="relative group">
                        <div className="aspect-square rounded-lg overflow-hidden bg-secondary">
                          <img
                            src={photo.photoUrl}
                            alt={photo.dogName}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex flex-col items-center justify-center">
                          <p className="text-white text-sm font-medium mb-2">{photo.dogName}</p>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={() => deletePhoto(photo.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
